import { ColDef } from 'ag-grid-community';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Icon, Typography } from '@mui/material';
import { Download as DownloadFileIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { DATE_FORMATS } from '@callmart/types';
import CRUDSettings from '../components/General/CRUDSettings';
import {
    useCreateHoliday,
    useDeleteHoliday,
    useExportPublicHolidays,
    usePublicHolidays,
    useUpdateHoliday,
    useUploadPublicHolidays,
} from '../hooks/api/publicHolidays';
import HolidayDialog from '../components/Holidays/HolidayDialog';
import { Holiday, HolidayForm } from '../types/Holiday.interface';
import ConfirmationDialog from '../components/UI/Dialogs/ConfirmationDialog';
import UploadXlsxFileButton from '../components/Buttons/UploadXlsxFileButton';
import { CheckboxCellRenderer } from '../components/AgGrid/CheckboxCellRenderer/CheckboxCellRenderer';

const Holidays = () => {
    const { t } = useTranslation();
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [selectedHoliday, setSelectedHoliday] = useState<Holiday | undefined>(undefined);

    const [holidays, loading] = usePublicHolidays();
    const { mutate: handleCreateHoliday } = useCreateHoliday({
        onSettled: () => {},
    });
    const { mutate: handleUpdateHoliday } = useUpdateHoliday({
        onSettled: () => {},
    });
    const { mutate: handleDeleteHoliday } = useDeleteHoliday({
        onSettled: (data, error) => {
            if (!error) {
                setSelectedHoliday(undefined);
                setShowConfirmationDialog(false);
            }
        },
    });
    const { mutateAsync: uploadPublicHolidaysFile, isLoading: isUploading } = useUploadPublicHolidays();
    const { mutateAsync: exportPublicHolidaysFile, isLoading: isExporting } = useExportPublicHolidays();

    const handleUploadFile = useCallback(
        async (files: File[], closeHandler: () => void) => {
            if (files.length > 0) {
                const data = await uploadPublicHolidaysFile(files[0]);
                setSelectedHoliday(undefined);
                setShowDialog(false);
                closeHandler();
                console.log(data);
            }
        },
        [uploadPublicHolidaysFile]
    );

    const handleDialogSubmit = useCallback(
        (data: HolidayForm) => {
            if (selectedHoliday) {
                handleUpdateHoliday({
                    id: selectedHoliday.id,
                    name: data.name,
                    date: typeof data.date === 'string' ? data.date : data.date.format(DATE_FORMATS.ISO_DATE),
                    weight: data.weight,
                    national: data.national,
                });
            } else {
                handleCreateHoliday({
                    name: data.name,
                    date: typeof data.date === 'string' ? data.date : data.date.format(DATE_FORMATS.ISO_DATE),
                    weight: data.weight,
                    national: data.national,
                });
            }
            setShowDialog(false);
        },
        [handleCreateHoliday, handleUpdateHoliday, selectedHoliday]
    );

    const onDeleteHoliday = useCallback(async (data: Holiday) => {
        setSelectedHoliday(data);
        setShowConfirmationDialog(true);
    }, []);

    const confirmHolidayDeletion = useCallback(
        (data: Holiday) => {
            handleDeleteHoliday(data.id);
            setShowConfirmationDialog(false);
        },
        [handleDeleteHoliday]
    );

    const columnDefs: ColDef[] = [
        {
            field: 'name',
            headerName: t('field.name'),
        },
        {
            field: 'date',
            headerName: t('field.date'),
            valueFormatter: ({ value }) => {
                return (value || '').split('-').reverse().join('.');
            },
        },
        {
            field: 'weight',
            headerName: t('field.weight'),
        },
        {
            field: 'national',
            headerName: t('holiday.national'),
            cellRenderer: CheckboxCellRenderer,
            cellRendererParams: {
                propertyName: 'national',
            },
        },
    ];

    return (
        <>
            <Grid
                container
                marginTop={1}
                marginBottom={-2}
                paddingX={2}
                spacing={2}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                <Grid item>
                    <Button onClick={() => setShowDialog(true)} variant="contained">
                        {t('holiday.add')}
                    </Button>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 1 }}>
                    <LoadingButton variant="contained" onClick={() => exportPublicHolidaysFile()} loading={isExporting}>
                        <Icon component={DownloadFileIcon} fontSize={'small'} sx={{ marginRight: 1 }} />
                        <Typography>{t('action.downloadExcel')}</Typography>
                    </LoadingButton>
                    <UploadXlsxFileButton
                        buttonName={
                            <>
                                <Icon component={UploadFileIcon} fontSize={'small'} sx={{ marginRight: 1 }} />
                                <Typography>{t('action.uploadExcel')}</Typography>
                            </>
                        }
                        uploadFiles={handleUploadFile}
                        dialogTitle={t('action.uploadExcelFile')}
                        loading={isUploading}
                    />
                </Grid>
            </Grid>

            <CRUDSettings
                tableRows={holidays}
                columnDefs={columnDefs}
                btnText={t('holiday.add')}
                hideButton={true}
                createRecord={handleCreateHoliday}
                updateRecord={handleUpdateHoliday}
                recordType={t('holiday.label')}
                isCreationViaDialog={true}
                isEditViaDialog={true}
                setShowCreationDialog={setShowDialog}
                loading={loading}
                onSelectedRowChange={setSelectedHoliday}
                showDeleteButton={true}
                onDelete={onDeleteHoliday}
                hideIdColumn={true}
            />
            {showDialog && (
                <HolidayDialog
                    holiday={selectedHoliday}
                    showDialog={showDialog}
                    onSubmit={handleDialogSubmit}
                    isLoading={false}
                    onClose={() => setShowDialog(false)}
                />
            )}
            {showConfirmationDialog && selectedHoliday && (
                <ConfirmationDialog
                    open={showConfirmationDialog}
                    title={t('holiday.delete')}
                    text={t('holiday.deleteConfirm', {
                        holiday: selectedHoliday.name,
                    })}
                    onClose={() => setShowConfirmationDialog(false)}
                    onConfirm={() => confirmHolidayDeletion(selectedHoliday)}
                />
            )}
        </>
    );
};

export default Holidays;
